export const COPQ_INPUT_MODES = {
  SIMPLE: "simple",
  DETAILED: "detailed",
};

export const BENEFIT_CLASSIFICATIONS = {
  ALL_HARD: "all_hard",
  MIXED: "mixed",
  ALL_SOFT: "all_soft",
};

export const FINANCIAL_STATUSES = {
  ESTIMATED: "estimated",
  VALIDATED: "validated",
};

export const COPQ_BREAKDOWN_FIELDS = [
  { key: "scrap", label: "Scrap" },
  { key: "rework_labor", label: "Rework labor" },
  { key: "throughput_capacity_loss", label: "Throughput/capacity loss" },
  { key: "expediting_delivery_recovery", label: "Expediting/delivery recovery" },
  { key: "customer_complaint_quality_support", label: "Customer complaint/quality support" },
  { key: "other", label: "Other" },
];

export function createEmptyCopqBreakdown() {
  return COPQ_BREAKDOWN_FIELDS.reduce((acc, field) => {
    acc[field.key] = "";
    return acc;
  }, {});
}

export function normalizeCopqBreakdown(rawBreakdown) {
  const emptyBreakdown = createEmptyCopqBreakdown();

  return COPQ_BREAKDOWN_FIELDS.reduce((acc, field) => {
    const value = rawBreakdown?.[field.key];
    acc[field.key] = value ?? emptyBreakdown[field.key];
    return acc;
  }, {});
}

export function sumCopqBreakdown(breakdown) {
  return COPQ_BREAKDOWN_FIELDS.reduce((sum, field) => {
    const value = Number(breakdown?.[field.key] ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
}

export function hasCopqBreakdownValues(breakdown) {
  return COPQ_BREAKDOWN_FIELDS.some((field) => {
    const rawValue = breakdown?.[field.key];
    return rawValue !== "" && rawValue != null && Number(rawValue) > 0;
  });
}

export function normalizeFinancialImpactForm(rawFinancialImpact) {
  const breakdown = normalizeCopqBreakdown(rawFinancialImpact?.copq_breakdown);
  const derivedMode = rawFinancialImpact?.copq_input_mode
    ?? (hasCopqBreakdownValues(breakdown) ? COPQ_INPUT_MODES.DETAILED : COPQ_INPUT_MODES.SIMPLE);
  const derivedCurrentCopq = derivedMode === COPQ_INPUT_MODES.DETAILED
    ? String(sumCopqBreakdown(breakdown))
    : rawFinancialImpact?.copq_current ?? "";

  return {
    financial_status: rawFinancialImpact?.financial_status ?? FINANCIAL_STATUSES.ESTIMATED,
    copq_input_mode: derivedMode,
    copq_breakdown: breakdown,
    copq_current: derivedCurrentCopq,
    copq_target: rawFinancialImpact?.copq_target ?? "",
    implementation_cost: rawFinancialImpact?.implementation_cost ?? "",
    benefit_classification: rawFinancialImpact?.benefit_classification ?? BENEFIT_CLASSIFICATIONS.ALL_HARD,
    hard_savings: rawFinancialImpact?.hard_savings ?? "",
    soft_savings: rawFinancialImpact?.soft_savings ?? "",
    notes: rawFinancialImpact?.notes ?? "",
  };
}

export function prepareFinancialImpactForSave(financialImpact) {
  const breakdown = normalizeCopqBreakdown(financialImpact.copq_breakdown);
  const copqInputMode = financialImpact.copq_input_mode ?? COPQ_INPUT_MODES.SIMPLE;
  const detailedTotal = sumCopqBreakdown(breakdown);

  return {
    ...financialImpact,
    financial_status: financialImpact.financial_status ?? FINANCIAL_STATUSES.ESTIMATED,
    copq_input_mode: copqInputMode,
    copq_current: copqInputMode === COPQ_INPUT_MODES.DETAILED ? detailedTotal : Number(financialImpact.copq_current),
    hard_savings: financialImpact.hard_savings === "" || financialImpact.hard_savings == null ? null : Number(financialImpact.hard_savings),
    soft_savings: financialImpact.soft_savings === "" || financialImpact.soft_savings == null ? null : Number(financialImpact.soft_savings),
    copq_breakdown: COPQ_BREAKDOWN_FIELDS.reduce((acc, field) => {
      const value = breakdown[field.key];
      acc[field.key] = value === "" || value == null ? 0 : Number(value);
      return acc;
    }, {}),
  };
}

export function buildAiCopqBreakdown(aiFinancialSuggestion) {
  const breakdown = createEmptyCopqBreakdown();

  for (const category of aiFinancialSuggestion?.copq_categories ?? []) {
    const categoryName = String(category.category || "").toLowerCase();
    const cost = Number(category.estimated_annual_cost ?? 0);
    if (!Number.isFinite(cost) || cost <= 0) continue;

    let key = "other";
    if (categoryName.includes("scrap")) key = "scrap";
    else if (categoryName.includes("rework")) key = "rework_labor";
    else if (categoryName.includes("throughput") || categoryName.includes("capacity")) key = "throughput_capacity_loss";
    else if (categoryName.includes("expedit") || categoryName.includes("delivery")) key = "expediting_delivery_recovery";
    else if (categoryName.includes("complaint") || categoryName.includes("support") || categoryName.includes("customer")) key = "customer_complaint_quality_support";

    const nextValue = Number(breakdown[key] || 0) + cost;
    breakdown[key] = String(nextValue);
  }

  return breakdown;
}

export function validateFinancialImpactForm(financialImpact) {
  const errors = {};

  const copqCurrentRaw = financialImpact.copq_input_mode === COPQ_INPUT_MODES.DETAILED
    ? sumCopqBreakdown(financialImpact.copq_breakdown)
    : financialImpact.copq_current;
  const copqTargetRaw = financialImpact.copq_target;
  const implementationCostRaw = financialImpact.implementation_cost;

  if (copqCurrentRaw === "" || copqCurrentRaw == null) {
    errors.copq_current = "Enter the current COPQ.";
  }

  if (copqTargetRaw === "" || copqTargetRaw == null) {
    errors.copq_target = "Enter the target COPQ.";
  }

  if (implementationCostRaw === "" || implementationCostRaw == null) {
    errors.implementation_cost = "Enter the implementation cost.";
  }

  const copqCurrent = Number(copqCurrentRaw);
  const copqTarget = Number(copqTargetRaw);
  const implementationCost = Number(implementationCostRaw);

  if (!errors.copq_current && (!Number.isFinite(copqCurrent) || copqCurrent <= 0)) {
    errors.copq_current = "Current COPQ must be greater than 0.";
  }

  if (!errors.copq_target && (!Number.isFinite(copqTarget) || copqTarget < 0)) {
    errors.copq_target = "Target COPQ cannot be negative.";
  }

  if (!errors.implementation_cost && (!Number.isFinite(implementationCost) || implementationCost < 0)) {
    errors.implementation_cost = "Implementation cost cannot be negative.";
  }

  if (!errors.copq_current && !errors.copq_target && copqTarget >= copqCurrent) {
    errors.copq_target = "Target COPQ must be less than Current COPQ.";
  }

  const annualSavings = !errors.copq_current && !errors.copq_target ? copqCurrent - copqTarget : null;
  const hardSavingsRaw = financialImpact.hard_savings;
  const softSavingsRaw = financialImpact.soft_savings;
  const hardSavings = hardSavingsRaw === "" || hardSavingsRaw == null ? null : Number(hardSavingsRaw);
  const softSavings = softSavingsRaw === "" || softSavingsRaw == null ? null : Number(softSavingsRaw);

  if (financialImpact.benefit_classification === BENEFIT_CLASSIFICATIONS.MIXED) {
    if (hardSavings == null) {
      errors.hard_savings = "Enter the hard savings portion.";
    } else if (!Number.isFinite(hardSavings) || hardSavings < 0) {
      errors.hard_savings = "Hard savings cannot be negative.";
    }

    if (softSavings == null) {
      errors.soft_savings = "Enter the soft savings portion.";
    } else if (!Number.isFinite(softSavings) || softSavings < 0) {
      errors.soft_savings = "Soft savings cannot be negative.";
    }

    if (
      annualSavings != null
      && !errors.hard_savings
      && !errors.soft_savings
      && Math.abs((hardSavings + softSavings) - annualSavings) > 0.01
    ) {
      errors.soft_savings = "Hard and soft savings must add up to Annual Savings.";
    }
  }

  return errors;
}

export function normalizeFinancialImpactResponse(data) {
  if (!data) return null;
  if (data.financial_impact) return data.financial_impact;
  if (data.calculated) return { ...data, ...data.calculated };
  return data;
}

export const PROJECTED_IMPACT_HEALTH_THRESHOLDS = {
  strong: {
    minRoiPercent: 200,
    maxPaybackMonths: 12,
  },
  moderate: {
    minRoiPercent: 50,
    maxPaybackMonths: 24,
  },
};

export function getProjectedImpactMetrics(calculatedFinancials) {
  if (!calculatedFinancials) return null;

  const annualSavings = calculatedFinancials.annual_savings
    ?? calculatedFinancials.gross_savings
    ?? calculatedFinancials.estimated_savings;
  const financialStatus = calculatedFinancials.financial_status
    ?? (calculatedFinancials.financial_validated ? FINANCIAL_STATUSES.VALIDATED : FINANCIAL_STATUSES.ESTIMATED);
  const netBenefit = calculatedFinancials.net_benefit ?? null;
  const roiPercent = calculatedFinancials.roi_percent ?? calculatedFinancials.roi_percentage ?? null;
  const paybackMonths = calculatedFinancials.payback_period_months ?? calculatedFinancials.payback_months ?? null;
  const hardSavings = calculatedFinancials.hard_savings ?? null;
  const softSavings = calculatedFinancials.soft_savings ?? null;
  const totalProjectedBenefit = calculatedFinancials.total_projected_benefit ?? annualSavings;

  if (annualSavings == null || netBenefit == null) {
    return null;
  }

  return {
    annualSavings,
    financialStatus,
    netBenefit,
    roiPercent,
    paybackMonths,
    hardSavings,
    softSavings,
    totalProjectedBenefit,
  };
}

export function getProjectedImpactHealth(metrics, thresholds = PROJECTED_IMPACT_HEALTH_THRESHOLDS) {
  if (!metrics) return null;

  const { roiPercent, paybackMonths } = metrics;

  if (
    roiPercent != null
    && paybackMonths != null
    && roiPercent >= thresholds.strong.minRoiPercent
    && paybackMonths <= thresholds.strong.maxPaybackMonths
  ) {
    return "Strong";
  }

  if (
    roiPercent != null
    && paybackMonths != null
    && roiPercent >= thresholds.moderate.minRoiPercent
    && paybackMonths <= thresholds.moderate.maxPaybackMonths
  ) {
    return "Moderate";
  }

  return "Weak";
}
